/* eslint-disable jsx-a11y/label-has-associated-control, jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-static-element-interactions */
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getShowroomDealer, updateShowroomDealer } from '../slice/showroomDealerSlice';
import { getShowroomCategory } from '../slice/showroomCategorySlice';
import { toast } from 'react-toastify';
import { FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import axiosInstance from '../apis/axiosConfig';
import './EditDealerStore.css';
import { validateImageFile } from '../utils/fileValidation';

const EditDealerStore = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { data: dealers = [] } = useSelector((state) => state.showroomDealer);
  const { data: categories = [] } = useSelector((state) => state.showroomCategory);

  const [countriesList, setCountriesList] = useState([]);
  const [statesList, setStatesList] = useState([]);
  const [activeTab, setActiveTab] = useState('details');

  const [formData, setFormData] = useState({
    id: null,
    showroom: '',
    category_id: '',
    sub_category_id: '',
    email_contact: '',
    phone_contact: '',
    seo_url: '',
    address: '',
    location: '',
    pincode: '',
    state_id: '',
    country_id: '101',
    banners: [],
    banner_status: 1,
    address_status: 1,
    map_info: {
      id: '0',
      location_type: 'location',
      geo_location: '',
      latitude: '',
      longitude: '',
      status: '1',
      sort: '1'
    },
    map_status: 1,
    seo_info: {
      meta_title: '',
      meta_description: '',
      meta_keywords: '',
      meta_tags: ''
    },
    seo_status: 1
  });

  const fileInputRefs = useRef({});

  useEffect(() => {
    if (dealers.length === 0) {
      dispatch(getShowroomDealer());
    }
    if (categories.length === 0) {
      dispatch(getShowroomCategory());
    }

    const apiBase = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000/api/v1').replace(/\/+$/, '');
    fetch(`${apiBase}/showroom-dealers/countries`)
      .then((res) => res.json())
      .then((data) => setCountriesList(data))
      .catch((err) => console.error('Error fetching countries:', err));

    fetch(`${apiBase}/showroom-dealers/states`)
      .then((res) => res.json())
      .then((data) => setStatesList(data))
      .catch((err) => console.error('Error fetching states:', err));
  }, [dispatch, dealers.length, categories.length]);

  useEffect(() => {
    if (dealers.length > 0) {
      const dealer = dealers.find((d) => d.id === parseInt(id));
      if (dealer) {
        setFormData({
          id: dealer.id,
          showroom: dealer.showroom || '',
          category_id: dealer.category_id || '',
          sub_category_id: dealer.sub_category_id || '',
          email_contact: dealer.email_contact || '',
          phone_contact: dealer.phone_contact || '',
          seo_url: dealer.seo_url || '',
          address: dealer.address || '',
          location: dealer.location || '',
          pincode: dealer.pincode || '',
          state_id: dealer.state_id || '',
          country_id: dealer.country_id || '101',
          banners: (dealer.banners || []).map((b, idx) => ({
            id: b.id || String(idx),
            heading: b.heading || '',
            description: b.description || '',
            image: b.image || '',
            alt: b.alt || '',
            status: b.status !== undefined ? b.status : 1,
            sort: b.sort || String(idx + 1)
          })),
          banner_status: dealer.banner_status !== undefined ? dealer.banner_status : 1,
          address_status: dealer.address_status !== undefined ? dealer.address_status : 1,
          map_info: {
            id: dealer.map_info?.id || '0',
            location_type: dealer.map_info?.location_type || 'location',
            geo_location: dealer.map_info?.geo_location || '',
            latitude: dealer.map_info?.latitude || '',
            longitude: dealer.map_info?.longitude || '',
            status: dealer.map_info?.status || '1',
            sort: dealer.map_info?.sort || '1'
          },
          map_status: dealer.map_status !== undefined ? dealer.map_status : 1,
          seo_info: {
            meta_title: dealer.seo_info?.meta_title || '',
            meta_description: dealer.seo_info?.meta_description || '',
            meta_keywords: dealer.seo_info?.meta_keywords || '',
            meta_tags: dealer.seo_info?.meta_tags || ''
          },
          seo_status: dealer.seo_status !== undefined ? dealer.seo_status : 1
        });
      }
    }
  }, [id, dealers]);

  const handleBannerChange = (index, field, value) => {
    const updatedBanners = [...formData.banners];
    updatedBanners[index] = { ...updatedBanners[index], [field]: value };
    setFormData({ ...formData, banners: updatedBanners });
  };

  const handleAddBanner = () => {
    setFormData({
      ...formData,
      banners: [
        ...formData.banners,
        {
          id: Date.now().toString(),
          image: '',
          alt: '',
          heading: '',
          description: '',
          status: 1,
          sort: String(formData.banners.length + 1)
        }
      ]
    });
  };

  const handleRemoveBanner = (index) => {
    const updatedBanners = [...formData.banners];
    updatedBanners.splice(index, 1);
    setFormData({ ...formData, banners: updatedBanners });
  };

  const handleImageUpload = async (index, e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!validateImageFile(file, e)) return;

    const uploadData = new FormData();
    uploadData.append('image', file);

    try {
      const res = await axiosInstance.post('/products/upload', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const filename = res.data.filename;
      handleBannerChange(index, 'image', filename);
      toast.success('Image uploaded successfully!');
    } catch (error) {
      console.error('Upload failed', error);
      toast.error('Image upload failed');
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleMapChange = (field, value) => {
    let updatedMapInfo = { ...formData.map_info, [field]: value };
    if (field === 'geo_location' && value) {
      const m1 = value.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
      const m2 = value.match(/[?&]q=(-?\d+\.\d+),(-?\d+\.\d+)/);
      const m3 = value.match(/place\/(-?\d+\.\d+),(-?\d+\.\d+)/);
      const m4 = value.match(/query=(-?\d+\.\d+),(-?\d+\.\d+)/);
      const m = m1 || m2 || m3 || m4;
      if (m) {
        updatedMapInfo.latitude = m[1];
        updatedMapInfo.longitude = m[2];
      }
    }
    setFormData({
      ...formData,
      map_info: updatedMapInfo
    });
  };

  const handleSeoChange = (field, value) => {
    setFormData({
      ...formData,
      seo_info: { ...formData.seo_info, [field]: value }
    });
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    if (!formData.showroom || !formData.category_id) {
      toast.error('Please fill all required fields in Showroom Details');
      return;
    }

    dispatch(updateShowroomDealer({ id: formData.id, data: formData }));
    toast.success('Showroom updated successfully!');
    navigate('/dealer-store');
  };

  const getImageUrl = (image) => {
    if (!image) return null;
    if (image.startsWith('data:')) return image;
    const backendHost = process.env.REACT_APP_BACKEND
      ? process.env.REACT_APP_BACKEND.replace(/\/$/, '')
      : (process.env.REACT_APP_BACKEND_URL || '').split('/api')[0] || 'http://localhost:8000';
    return `${backendHost}/uploads/${image}`;
  };

  // Validation functions for sidebar badges
  const isBasicInfoValid = () => {
    return !!(formData.showroom && formData.category_id);
  };

  const isBannerValid = () => {
    if (formData.banners.length === 0) return false;
    return formData.banners.every((b) => b.image && b.alt);
  };

  const isAddressValid = () => {
    return !!(formData.address && formData.location && formData.pincode && formData.state_id && formData.country_id);
  };

  const isMapValid = () => {
    const map = formData.map_info || {};
    if (map.location_type === 'coordinates') {
      return !!(map.latitude && map.longitude);
    }
    return !!map.geo_location;
  };

  const isSeoValid = () => {
    const seo = formData.seo_info || {};
    return !!(seo.meta_title && seo.meta_description);
  };

  const mainCategoriesList = (Array.isArray(categories) ? categories : []).filter((c) => c.category_type === 0);
  const subCategoriesList = (Array.isArray(categories) ? categories : []).filter(
    (c) => c.category_type !== 0 && c.parent_category_name === mainCategoriesList.find((m) => m.id == formData.category_id)?.category
  );
  const filteredStatesList = statesList.filter((s) => s.country_id == formData.country_id);

  return (
    <div className="edit-showroom-container animate-fade-up">
      <div className="edit-showroom-header">
        <h1 className="edit-showroom-title">Modify Showroom Details</h1>
        <button className="btn-premium-outline" onClick={() => navigate('/dealer-store')}>
          Back to Store
        </button>
      </div>

      <div className="edit-showroom-layout">
        {/* Sidebar Navigation */}
        <aside className="edit-showroom-sidebar">
          <ul className="sidebar-menu">
            <li className={`sidebar-item ${activeTab === 'details' ? 'active' : ''}`}>
              <div className="sidebar-item-clickable" onClick={() => setActiveTab('details')}>
                <span>Basic Info</span>
              </div>
              <div className="sidebar-item-controls">
                {isBasicInfoValid() ? (
                  <FaCheckCircle className="validation-icon valid" />
                ) : (
                  <FaExclamationCircle className="validation-icon warning" />
                )}
                <label className="toggle-switch small">
                  <input type="checkbox" checked disabled />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </li>

            <li className={`sidebar-item ${activeTab === 'banner' ? 'active' : ''}`}>
              <div className="sidebar-item-clickable" onClick={() => setActiveTab('banner')}>
                <span>Banner Configuration</span>
              </div>
              <div className="sidebar-item-controls">
                {isBannerValid() ? (
                  <FaCheckCircle className="validation-icon valid" />
                ) : (
                  <FaExclamationCircle className="validation-icon warning" />
                )}
                <label className="toggle-switch small">
                  <input
                    type="checkbox"
                    checked={formData.banner_status === 1}
                    onChange={(e) => setFormData({ ...formData, banner_status: e.target.checked ? 1 : 0 })}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </li>

            <li className={`sidebar-item ${activeTab === 'address' ? 'active' : ''}`}>
              <div className="sidebar-item-clickable" onClick={() => setActiveTab('address')}>
                <span>Address Manager</span>
              </div>
              <div className="sidebar-item-controls">
                {isAddressValid() ? (
                  <FaCheckCircle className="validation-icon valid" />
                ) : (
                  <FaExclamationCircle className="validation-icon warning" />
                )}
                <label className="toggle-switch small">
                  <input
                    type="checkbox"
                    checked={formData.address_status === 1}
                    onChange={(e) => setFormData({ ...formData, address_status: e.target.checked ? 1 : 0 })}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </li>

            <li className={`sidebar-item ${activeTab === 'map' ? 'active' : ''}`}>
              <div className="sidebar-item-clickable" onClick={() => setActiveTab('map')}>
                <span>Map Location</span>
              </div>
              <div className="sidebar-item-controls">
                {isMapValid() ? (
                  <FaCheckCircle className="validation-icon valid" />
                ) : (
                  <FaExclamationCircle className="validation-icon warning" />
                )}
                <label className="toggle-switch small">
                  <input
                    type="checkbox"
                    checked={formData.map_status === 1}
                    onChange={(e) => setFormData({ ...formData, map_status: e.target.checked ? 1 : 0 })}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </li>

            <li className={`sidebar-item ${activeTab === 'seo' ? 'active' : ''}`}>
              <div className="sidebar-item-clickable" onClick={() => setActiveTab('seo')}>
                <span>SEO Manager</span>
              </div>
              <div className="sidebar-item-controls">
                {isSeoValid() ? (
                  <FaCheckCircle className="validation-icon valid" />
                ) : (
                  <FaExclamationCircle className="validation-icon warning" />
                )}
                <label className="toggle-switch small">
                  <input
                    type="checkbox"
                    checked={formData.seo_status === 1}
                    onChange={(e) => setFormData({ ...formData, seo_status: e.target.checked ? 1 : 0 })}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </li>
          </ul>
        </aside>

        {/* Main Content Area */}
        <div className="edit-showroom-content">
          <div className="edit-showroom-card">
            {activeTab === 'details' && (
              <form onSubmit={handleSubmit} className="premium-form-full">
                <h2 className="form-section-title">Basic Info</h2>

                <div className="form-row">
                  <div className="form-group flex-1">
                    <label>
                      Category <span className="required">*</span>
                    </label>
                    <div className="premium-select-wrapper">
                      <select name="category_id" value={formData.category_id} onChange={handleInputChange} required>
                        <option value="">Select Category</option>
                        {mainCategoriesList.map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.category}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-group flex-1">
                    <label>
                      Sub Category <span className="required">*</span>
                    </label>
                    <div className="premium-select-wrapper">
                      <select name="sub_category_id" value={formData.sub_category_id} onChange={handleInputChange} required>
                        <option value="">Select Sub Category</option>
                        {subCategoriesList.map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.category}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label>
                    Showroom Name <span className="required">*</span>
                  </label>
                  <div className={`input-with-warning ${formData.showroom.length > 100 ? 'has-error' : ''}`}>
                    <input
                      type="text"
                      name="showroom"
                      value={formData.showroom}
                      onChange={handleInputChange}
                      placeholder="Showroom Name"
                      required
                    />
                  </div>
                  {formData.showroom.length > 100 && <small className="error-text">Please enter no more than 100 characters.</small>}
                </div>

                <div className="form-row">
                  <div className="form-group flex-1">
                    <label>Phone</label>
                    <input
                      type="text"
                      name="phone_contact"
                      value={formData.phone_contact}
                      onChange={handleInputChange}
                      placeholder="Phone Contact"
                    />
                  </div>
                  <div className="form-group flex-1">
                    <label>Email</label>
                    <input
                      type="email"
                      name="email_contact"
                      value={formData.email_contact}
                      onChange={handleInputChange}
                      placeholder="Email Contact"
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button type="button" className="btn-premium-outline" onClick={() => navigate('/dealer-store')}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-premium" disabled={formData.showroom.length > 100}>
                    Save
                  </button>
                </div>
              </form>
            )}

            {activeTab === 'banner' && (
              <div className="banner-config-container">
                {formData.banners.map((banner, index) => (
                  <div key={index} className="banner-slide-card">
                    <div className="banner-slide-header">
                      <h4>Slide {index + 1}</h4>
                      <div className="banner-slide-actions">
                        <label className="toggle-switch">
                          <input
                            type="checkbox"
                            checked={banner.status === 1}
                            onChange={(e) => handleBannerChange(index, 'status', e.target.checked ? 1 : 0)}
                          />
                          <span className="toggle-slider"></span>
                        </label>
                        <span className="toggle-label">{banner.status === 1 ? 'Active' : 'Inactive'}</span>
                        <button type="button" className="action-icon-btn delete ml-2" onClick={() => handleRemoveBanner(index)}>
                          🗑️
                        </button>
                      </div>
                    </div>

                    <div className="banner-slide-body">
                      <div className="banner-image-section">
                        <label>
                          Banner Image <span className="required">*</span>
                        </label>
                        <div className="image-upload-box" onClick={() => fileInputRefs.current[index]?.click()}>
                          {banner.image ? (
                            <img src={getImageUrl(banner.image)} alt="Banner Preview" className="banner-preview" />
                          ) : (
                            <div className="upload-placeholder">
                              <span style={{ fontSize: '2rem' }}>🖼️</span>
                              <p>Upload Image</p>
                            </div>
                          )}
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          ref={(el) => (fileInputRefs.current[index] = el)}
                          onChange={(e) => handleImageUpload(index, e)}
                        />
                        <div className="form-group" style={{ marginTop: '1rem' }}>
                          <label>
                            Alt Text <span className="required">*</span>
                          </label>
                          <input
                            type="text"
                            value={banner.alt}
                            onChange={(e) => handleBannerChange(index, 'alt', e.target.value)}
                            placeholder="e.g. showroom-banner"
                          />
                        </div>
                      </div>

                      <div className="banner-text-section">
                        <div className="form-group">
                          <label>
                            Banner Title <span>(Optional)</span>
                          </label>
                          <input
                            type="text"
                            value={banner.heading}
                            onChange={(e) => handleBannerChange(index, 'heading', e.target.value)}
                            placeholder="Banner Title"
                          />
                        </div>
                        <div className="form-group" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                          <label>
                            Banner Description <span>(Optional)</span>
                          </label>
                          <textarea
                            value={banner.description}
                            onChange={(e) => handleBannerChange(index, 'description', e.target.value)}
                            placeholder="Banner Description"
                            maxLength={240}
                            style={{ flex: 1, minHeight: '150px' }}
                          />
                          <small style={{ color: '#a0a0a0', marginTop: '0.5rem' }}>Note: Max length: 240 Characters</small>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                <button type="button" className="btn-add-more" onClick={handleAddBanner}>
                  + Add More Slide
                </button>

                <div className="form-actions">
                  <button type="button" className="btn-premium-outline" onClick={() => navigate('/dealer-store')}>
                    Cancel
                  </button>
                  <button type="button" className="btn-premium" onClick={handleSubmit}>
                    Save Changes
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'address' && (
              <div className="address-manager-container">
                <h2 className="form-section-title">Address Manager</h2>
                <div className="premium-form-full">
                  <div className="form-group">
                    <label>
                      Address <span className="required">*</span>
                    </label>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="Cheranallur, Ernakulam, Kochi, Kerala 682034"
                      rows="3"
                      required
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group flex-1">
                      <label>
                        District / Location <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        name="location"
                        value={formData.location}
                        onChange={handleInputChange}
                        placeholder="kochi"
                        required
                      />
                    </div>
                    <div className="form-group flex-1">
                      <label>
                        PIN Code <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        name="pincode"
                        value={formData.pincode}
                        onChange={handleInputChange}
                        placeholder="682034"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group flex-1">
                      <label>
                        Country <span className="required">*</span>
                      </label>
                      <div className="premium-select-wrapper">
                        <select name="country_id" value={formData.country_id} onChange={handleInputChange} required>
                          <option value="">Select Country</option>
                          {countriesList.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="form-group flex-1">
                      <label>
                        State <span className="required">*</span>
                      </label>
                      <div className="premium-select-wrapper">
                        <select name="state_id" value={formData.state_id} onChange={handleInputChange} required>
                          <option value="">Select State</option>
                          {filteredStatesList.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="form-actions">
                    <button type="button" className="btn-premium-outline" onClick={() => navigate('/dealer-store')}>
                      Cancel
                    </button>
                    <button type="button" className="btn-premium" onClick={handleSubmit}>
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'map' && (
              <div className="map-location-container">
                <h2 className="form-section-title">Map Location</h2>
                <div className="premium-form-full">
                  <div className="form-group">
                    <label>Mark location by using:</label>
                    <div className="radio-group">
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="location_type"
                          value="location"
                          checked={formData.map_info?.location_type === 'location'}
                          onChange={() => handleMapChange('location_type', 'location')}
                        />
                        <span>Google map address</span>
                      </label>
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="location_type"
                          value="coordinates"
                          checked={formData.map_info?.location_type === 'coordinates'}
                          onChange={() => handleMapChange('location_type', 'coordinates')}
                        />
                        <span>Geo location coordinates</span>
                      </label>
                    </div>
                  </div>

                  {formData.map_info?.location_type === 'location' ? (
                    <div className="form-group">
                      <label>
                        Google map address <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.map_info?.geo_location || ''}
                        onChange={(e) => handleMapChange('geo_location', e.target.value)}
                        placeholder="https://maps.app.goo.gl/..."
                        required
                      />
                    </div>
                  ) : (
                    <div className="form-row">
                      <div className="form-group flex-1">
                        <label>
                          Latitude <span className="required">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.map_info?.latitude || ''}
                          onChange={(e) => handleMapChange('latitude', e.target.value)}
                          placeholder="e.g. 13.01869"
                          required
                        />
                      </div>
                      <div className="form-group flex-1">
                        <label>
                          Longitude <span className="required">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.map_info?.longitude || ''}
                          onChange={(e) => handleMapChange('longitude', e.target.value)}
                          placeholder="e.g. 77.61301"
                          required
                        />
                      </div>
                    </div>
                  )}

                  <div style={{ margin: '8px 0 16px 0', display: 'flex', gap: '10px' }}>
                    <button
                      type="button"
                      className="btn-premium-outline"
                      style={{ fontSize: '13px', padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '6px' }}
                      onClick={async () => {
                        try {
                          const stateObj = statesList.find(s => s.id == formData.state_id);
                          const stateName = stateObj?.name || '';
                          const rawAddress = formData.address || '';
                          const location = formData.location || '';
                          const pincode = formData.pincode || '';

                          if (!rawAddress && !location && !pincode) {
                            toast.warning('Please fill Address, City and Pincode in the Address tab first');
                            return;
                          }

                          toast.info('Auto-detecting exact location from address...');

                          // Helper: clean address parts (removes noise like "Plot No 24", "Shop No 1")
                          const rawParts = rawAddress.split(',').map(s => s.trim()).filter(Boolean);
                          const cleanedParts = rawParts.filter(p => {
                            const l = p.toLowerCase();
                            if (/^(plot|shop|flat|house|unit|survey|gala|khasra|h\.?no)\s*no\.?/i.test(l)) return false;
                            if (/^(ground|first|second|third|upper|lower)\s*floor/i.test(l)) return false;
                            if (/^\d+[/-]\d+/i.test(l)) return false;
                            return true;
                          });

                          // Extract dealer business name (e.g. "Grahom Creations" from "Aristo Atelier - Grahom Creations")
                          const rawShowroom = (formData.showroom || '').trim();
                          const businessName = rawShowroom.includes('-')
                            ? rawShowroom.split('-').slice(1).join('-').trim()
                            : rawShowroom;

                          const queries = [
                            businessName && businessName.length > 2 ? [businessName, rawAddress, location, stateName, pincode, 'India'].filter(Boolean).join(', ') : null,
                            rawShowroom && rawShowroom !== businessName ? [rawShowroom, rawAddress, location, stateName, pincode, 'India'].filter(Boolean).join(', ') : null,
                            businessName && location ? [businessName, location, stateName, pincode, 'India'].filter(Boolean).join(', ') : null,
                            [rawAddress, location, stateName, pincode, 'India'].filter(Boolean).join(', '),
                            [cleanedParts.join(' '), location, stateName, pincode, 'India'].filter(Boolean).join(' '),
                            cleanedParts.length >= 2 ? [cleanedParts[0], cleanedParts[1], location, pincode].filter(Boolean).join(' ') : null,
                            cleanedParts.length >= 1 ? [cleanedParts[0], location, pincode].filter(Boolean).join(' ') : null,
                            cleanedParts.length >= 2 ? [cleanedParts[cleanedParts.length - 1], location, pincode].filter(Boolean).join(' ') : null,
                            pincode ? `${pincode}, ${stateName || location}, India` : null,
                            pincode ? `${pincode} India` : null
                          ].filter(Boolean);

                          let found = null;

                          // Priority 1: Google Maps Geocoding API (100% exact Indian street & landmark accuracy)
                          const googleApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyChdrzPLyWDh3LkOPHqZ3fXHrnmjhOHfS0';
                          for (const q of queries) {
                            try {
                              const gRes = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(q)}&region=in&key=${googleApiKey}`);
                              if (gRes.ok) {
                                const gData = await gRes.json();
                                if (gData.status === 'OK' && gData.results && gData.results.length > 0) {
                                  const loc = gData.results[0].geometry.location;
                                  found = { lat: String(loc.lat), lon: String(loc.lng), name: gData.results[0].formatted_address };
                                  break;
                                }
                              }
                            } catch (e) {
                              // try next query
                            }
                          }

                          // Priority 2: Fallback to OpenStreetMap (Photon + Nominatim) if Google API unavailable
                          if (!found) {
                            for (const q of queries) {
                              try {
                                const pRes = await fetch(`https://photon.komoot.io/api/?q=${encodeURIComponent(q)}&limit=1`, {
                                  headers: { 'User-Agent': 'AristoAdmin/1.0' }
                                });
                                if (pRes.ok) {
                                  const pData = await pRes.json();
                                  const feat = pData.features?.[0];
                                  if (feat?.geometry?.coordinates) {
                                    const [lng, lat] = feat.geometry.coordinates;
                                    const name = feat.properties?.name || feat.properties?.street || feat.properties?.district || q;
                                    found = { lat: String(lat), lon: String(lng), name };
                                    break;
                                  }
                                }
                              } catch (e) {
                                // try next query
                              }

                              try {
                                const nRes = await fetch(`https://nominatim.openstreetmap.org/search?format=json&countrycodes=in&limit=1&q=${encodeURIComponent(q)}`, {
                                  headers: { 'User-Agent': 'AristoAdmin/1.0' }
                                });
                                if (nRes.ok) {
                                  const nData = await nRes.json();
                                  if (nData && nData.length > 0 && nData[0].lat && nData[0].lon) {
                                    found = { lat: String(nData[0].lat), lon: String(nData[0].lon), name: nData[0].display_name };
                                    break;
                                  }
                                }
                              } catch (e) {
                                // try next query
                              }
                            }
                          }

                          if (found) {
                            setFormData(prev => ({
                              ...prev,
                              map_info: {
                                ...prev.map_info,
                                location_type: 'coordinates',
                                latitude: String(found.lat),
                                longitude: String(found.lon),
                                geo_location: found.name || prev.map_info.geo_location
                              }
                            }));
                            toast.success(`📍 Located: ${found.name || 'Exact coordinates detected'} (${parseFloat(found.lat).toFixed(4)}, ${parseFloat(found.lon).toFixed(4)})`);
                          } else {
                            toast.warning('Could not pinpoint exact street. Please enter coordinates manually or paste Google Maps link.');
                          }
                        } catch (err) {
                          toast.error('Failed to geocode address: ' + err.message);
                        }
                      }}
                    >
                      📍 Auto-Detect Coordinates from Address
                    </button>
                  </div>

                  <div className="map-preview-container">
                    <label>Map Preview</label>
                    {(formData.map_info?.location_type === 'coordinates' && formData.map_info?.latitude && formData.map_info?.longitude) ||
                    (formData.map_info?.location_type === 'location' && formData.map_info?.geo_location) ? (
                      <iframe
                        title="Google Map Preview"
                        width="100%"
                        height="300"
                        style={{ border: '1px solid rgba(196, 168, 130, 0.2)', borderRadius: '8px' }}
                        loading="lazy"
                        allowFullScreen
                        src={`https://maps.google.com/maps?q=${encodeURIComponent(
                          formData.map_info.location_type === 'coordinates'
                            ? `${formData.map_info.latitude},${formData.map_info.longitude}`
                            : formData.map_info.geo_location
                        )}&t=&z=14&ie=UTF8&iwloc=&output=embed`}
                      />
                    ) : (
                      <div className="map-placeholder-box">No map address or coordinates provided for preview</div>
                    )}
                  </div>

                  <div className="form-actions">
                    <button type="button" className="btn-premium-outline" onClick={() => navigate('/dealer-store')}>
                      Cancel
                    </button>
                    <button type="button" className="btn-premium" onClick={handleSubmit}>
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'seo' && (
              <div className="seo-manager-container">
                <h2 className="form-section-title">SEO Manager</h2>
                <div className="premium-form-full">
                  <div className="form-group">
                    <label>
                      Meta Title <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.seo_info?.meta_title || ''}
                      onChange={(e) => handleSeoChange('meta_title', e.target.value)}
                      placeholder="Meta Title"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      Meta Description <span className="required">*</span>
                    </label>
                    <textarea
                      value={formData.seo_info?.meta_description || ''}
                      onChange={(e) => handleSeoChange('meta_description', e.target.value)}
                      placeholder="Meta Description"
                      rows="4"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Meta Keywords</label>
                    <input
                      type="text"
                      value={formData.seo_info?.meta_keywords || ''}
                      onChange={(e) => handleSeoChange('meta_keywords', e.target.value)}
                      placeholder="Enter keywords..."
                    />
                    <small style={{ color: '#a0a0a0', marginTop: '0.5rem' }}>Add commas to separate keywords</small>
                  </div>

                  <div className="form-actions">
                    <button type="button" className="btn-premium-outline" onClick={() => navigate('/dealer-store')}>
                      Cancel
                    </button>
                    <button type="button" className="btn-premium" onClick={handleSubmit}>
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditDealerStore;
